/**
 * Dialogs (web.md §4 "toast + Undo, dialogs"): a stack in the UI store, rendered by `DialogHost` from a
 * registry of kinds. Any area registers its dialogs once (`registerDialog('view-builder', ViewBuilder)`)
 * and opens them with `openDialog(kind, props)`. Markup is the prototype's native `<dialog>` with a
 * header (title + close tool) and a body; Escape and a click on the backdrop close the top dialog.
 *
 * Kinds the shell registers: 'confirm', 'upsell', 'shortcuts', 'unsaved-changes', 'settings-filter'.
 * Kinds the reader registers (WP11): 'view-builder', 'topics', 'list-filter', 'briefing'.
 */

import { useEffect, useRef, type ComponentType, type ReactNode } from 'react';
import { Tool } from '../ui/Tool.js';
import { ui, useUi, type DialogEntry } from './ui-store.js';

export interface DialogProps {
  /** Close this dialog. */
  close(): void;
  dialogId: number;
}

type AnyDialog = ComponentType<DialogProps & Record<string, unknown>>;

const registry = new Map<string, AnyDialog>();
let nextId = 1;

export function registerDialog<P extends object>(kind: string, component: ComponentType<DialogProps & P>): void {
  registry.set(kind, component as unknown as AnyDialog);
}

export function isDialogRegistered(kind: string): boolean {
  return registry.has(kind);
}

export function openDialog<P extends object>(kind: string, props?: P): number {
  const id = nextId++;
  const entry: DialogEntry = { id, kind, props: { ...(props ?? {}) } as Record<string, unknown> };
  ui.set((s) => ({ dialogs: [...s.dialogs, entry] }));
  return id;
}

export function closeDialog(id?: number): void {
  ui.set((s) => {
    if (s.dialogs.length === 0) return {};
    const target = id ?? s.dialogs[s.dialogs.length - 1]?.id;
    return { dialogs: s.dialogs.filter((d) => d.id !== target) };
  });
}

export function DialogHost() {
  const dialogs = useUi((s) => s.dialogs);
  return (
    <>
      {dialogs.map((entry) => {
        const Component = registry.get(entry.kind);
        if (!Component) return null;
        return <Component key={entry.id} {...entry.props} dialogId={entry.id} close={() => closeDialog(entry.id)} />;
      })}
    </>
  );
}

export interface DialogFrameProps {
  title: ReactNode;
  /** Extra class on the <dialog>: 'small-dialog', 'brief-dialog' … (the prototype's `kind`). */
  className?: string;
  onClose(): void;
  children: ReactNode;
  dialogId: number;
}

/** The prototype's dialog: `<dialog><header><h2/>close</header><div id="dialog-body">…</div></dialog>`. */
export function DialogFrame({ title, className, onClose, children, dialogId }: DialogFrameProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const first = useUi((s) => s.dialogs[0]?.id === dialogId);
  useEffect(() => {
    opener.current = document.activeElement;
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => {
      const back = opener.current;
      if (back instanceof HTMLElement && back.isConnected) back.focus();
    };
  }, []);
  const titleId = `dialog-title-${dialogId}`;
  return (
    <dialog
      ref={ref}
      id={first ? 'dialog' : undefined}
      className={className}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target !== ref.current) return;
        const b = ref.current.getBoundingClientRect();
        if (e.clientX < b.left || e.clientX > b.right || e.clientY < b.top || e.clientY > b.bottom) onClose();
      }}
    >
      <header>
        <h2 id={titleId}>{title}</h2>
        <Tool icon="x" label="Close" onClick={onClose} />
      </header>
      <div id={first ? 'dialog-body' : undefined} className="dialog-body">
        {children}
      </div>
    </dialog>
  );
}
